import React, { useState, useEffect } from "react";
import { Star, ThumbsUp, User, Loader2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const StarRating = ({ rating, onRate, interactive = false, size = "md" }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-6 h-6" : "w-5 h-5";
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors`}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hovered || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ProductReviews = ({ productId, productName }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    title: "",
    comment: ""
  });

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(response.data.reviews);
      setAverageRating(response.data.average_rating);
      setTotalReviews(response.data.total_reviews);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    if (!formData.title.trim() || !formData.comment.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        {
          product_id: productId,
          rating: formData.rating,
          title: formData.title,
          comment: formData.comment
        },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Review submitted! It will appear after approval.");
      setShowForm(false);
      setFormData({ rating: 0, title: "", comment: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => distribution[r.rating]++);
    return distribution;
  };

  const distribution = getRatingDistribution();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="product-reviews">
      <h3 className="font-heading text-xl font-bold text-gray-900 uppercase mb-6">
        Customer Reviews
      </h3>

      {/* Summary Section */}
      <div className="grid md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-200">
        {/* Average Rating */}
        <div className="text-center md:text-left">
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <span className="text-5xl font-bold text-gray-900">{averageRating.toFixed(1)}</span>
            <div>
              <StarRating rating={Math.round(averageRating)} size="lg" />
              <p className="text-gray-500 text-sm mt-1">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => (
            <div key={star} className="flex items-center gap-2 text-sm">
              <span className="w-4 text-gray-600">{star}</span>
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full transition-all"
                  style={{ width: totalReviews > 0 ? `${(distribution[star] / totalReviews) * 100}%` : '0%' }}
                />
              </div>
              <span className="w-8 text-gray-400 text-right">{distribution[star]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Write Review Button */}
      {isAuthenticated ? (
        !showForm ? (
          <Button
            onClick={() => setShowForm(true)}
            className="mb-6 bg-primary hover:bg-primary/90"
            data-testid="write-review-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Write a Review
          </Button>
        ) : (
          <form onSubmit={handleSubmitReview} className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-4">Write Your Review</h4>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Your Rating</label>
              <StarRating
                rating={formData.rating}
                onRate={(r) => setFormData({ ...formData, rating: r })}
                interactive
                size="lg"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Review Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Summarize your experience"
                className="bg-white border-gray-200"
                maxLength={100}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Your Review</label>
              <Textarea
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Share your experience with this product..."
                className="bg-white border-gray-200"
                rows={4}
                maxLength={1000}
              />
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Review"
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-gray-200">
                Cancel
              </Button>
            </div>
          </form>
        )
      ) : (
        <p className="mb-6 text-gray-500 text-sm">
          <a href="/auth" className="text-primary hover:underline">Login</a> to write a review
        </p>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No reviews yet</p>
          <p className="text-gray-400 text-sm">Be the first to review this product!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.review_id} className="border-b border-gray-100 pb-6 last:border-0" data-testid={`review-${review.review_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StarRating rating={review.rating} size="sm" />
                    <span className="font-medium text-gray-900">{review.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <User className="w-4 h-4" />
                    <span>{review.user_name}</span>
                    <span>•</span>
                    <span>{new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 mt-2">{review.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
